import React, { createContext, useContext, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { closeImageGenerator, openImageGenerator } from '../services/imageGeneratorSlice'

export interface ImageGeneratorSubmitData {
  imageUrl: string
  prompt: string
}

type ImageGeneratorSubmitCallback = (data: ImageGeneratorSubmitData) => Promise<void>

interface ImageGeneratorContextValue {
  showImageGenerator: (onSubmit: ImageGeneratorSubmitCallback) => void
  handleSubmit: (data: ImageGeneratorSubmitData) => Promise<void>
  handleCancel: () => void
}

const ImageGeneratorCallbackContext = createContext<ImageGeneratorContextValue | undefined>(undefined)

export const ImageGeneratorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const callbackRef = useRef<ImageGeneratorSubmitCallback | null>(null)
  const dispatch = useDispatch()

  const showImageGenerator = (onSubmit: ImageGeneratorSubmitCallback) => {
    callbackRef.current = onSubmit
    dispatch(openImageGenerator())
  }

  const handleSubmit = async (data: ImageGeneratorSubmitData) => {
    if (callbackRef.current) {
      try {
        await callbackRef.current(data)
      } finally {
        callbackRef.current = null
        dispatch(closeImageGenerator())
      }
    } else {
      dispatch(closeImageGenerator())
    }
  }

  const handleCancel = () => {
    callbackRef.current = null
    dispatch(closeImageGenerator())
  }

  return (
    <ImageGeneratorCallbackContext.Provider value={{ showImageGenerator, handleSubmit, handleCancel }}>
      {children}
    </ImageGeneratorCallbackContext.Provider>
  )
}

export function useImageGenerator() {
  const ctx = useContext(ImageGeneratorCallbackContext)
  if (!ctx) throw new Error('useImageGenerator must be used within ImageGeneratorProvider')
  return ctx.showImageGenerator
}

export function useImageGeneratorSubmitHandler() {
  const ctx = useContext(ImageGeneratorCallbackContext)
  if (!ctx) throw new Error('useImageGeneratorSubmitHandler must be used within ImageGeneratorProvider')
  return ctx.handleSubmit
}

export function useImageGeneratorCancelHandler() {
  const ctx = useContext(ImageGeneratorCallbackContext)
  if (!ctx) throw new Error('useImageGeneratorCancelHandler must be used within ImageGeneratorProvider')
  return ctx.handleCancel
}
